import { motion } from "framer-motion";
import { SealCheck, ChatCircleText, HandHeart, PiggyBank, ArrowUpRight } from "@phosphor-icons/react";

/**
 * "Book Direct & Save" — static price-comparison section.
 * Replaces the third-party Direct Booking Tools widget (incompatible with
 * Hospitable's iframe booking engine) with indexable, fully-owned content:
 * a worked example of what the same stay costs once each platform's guest
 * service fees / markups are added vs. booking direct at our listed rate.
 *
 * <BookDirectSave />          full two-panel section (property detail pages)
 * <BookDirectSave compact />  slim strip (homepage, under the booking bar)
 */

const PLATFORM_ROWS = [
  { id: "airbnb", name: "Airbnb", note: "Guest service fee up to ~14%", price: "$1,710" },
  { id: "vrbo", name: "Vrbo", note: "Guest service fee up to ~12%", price: "$1,680" },
  { id: "booking", name: "Booking.com", note: "Rates often marked up ~15%", price: "$1,725" },
];

const PERKS = [
  { icon: SealCheck, label: "Best rate — guaranteed" },
  { icon: ChatCircleText, label: "Chat directly with your hosts" },
  { icon: HandHeart, label: "Personal, flexible help if plans change" },
];

export const BookDirectSave = ({ compact = false, onCta }) => {
  if (compact) {
    return (
      <motion.div
        data-testid="book-direct-save-compact"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.6 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-2xl border border-emerald/10 bg-sand-2 px-6 py-5"
      >
        <p className="flex items-center gap-3 text-emerald font-medium">
          <span className="w-9 h-9 rounded-full bg-sunset/15 grid place-items-center shrink-0">
            <PiggyBank size={20} weight="fill" className="text-sunset" />
          </span>
          Book direct and skip the platform fees
        </p>
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
          <span className="rounded-full border border-emerald/15 px-3 py-1.5 text-emerald-soft">Airbnb + ~14% fees</span>
          <span className="rounded-full border border-emerald/15 px-3 py-1.5 text-emerald-soft">Vrbo + ~12% fees</span>
          <span className="rounded-full border border-emerald/15 px-3 py-1.5 text-emerald-soft">Booking.com ~15% markup</span>
          <span data-testid="book-direct-save-compact-direct" className="rounded-full bg-emerald px-3 py-1.5 text-sand">Direct — $0 fees</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      data-testid="book-direct-save"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="grid lg:grid-cols-[1fr_1.25fr] overflow-hidden rounded-2xl border border-emerald/10"
    >
      {/* Pitch panel */}
      <div className="bg-emerald text-sand p-8 md:p-12 flex flex-col justify-center">
        <p className="overline text-gold mb-4">Book direct & save</p>
        <h3 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight mb-5">
          Same stay.
          <br />
          No platform fees.
        </h3>
        <p className="text-sand/80 font-light leading-relaxed mb-8 max-w-md">
          Airbnb, Vrbo and Booking.com add their own fees and markups on top of our rates. Reserve right here and you skip them entirely.
        </p>
        <ul className="space-y-3.5 mb-10">
          {PERKS.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center gap-3 text-sm text-sand/90">
              <Icon size={20} weight="fill" className="text-gold shrink-0" />
              {label}
            </li>
          ))}
        </ul>
        {onCta && (
          <button
            type="button"
            data-testid="book-direct-save-cta"
            onClick={onCta}
            className="group w-fit inline-flex items-center gap-2 rounded-full bg-sunset px-7 py-3.5 text-sm font-medium text-sand transition-colors duration-300 hover:bg-sand hover:text-emerald"
          >
            Check dates & book direct
            <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        )}
      </div>

      {/* Comparison panel */}
      <div className="bg-sand-2 p-8 md:p-12">
        <p className="overline text-emerald-soft mb-6">What a $1,500 stay can cost</p>
        <div data-testid="book-direct-save-table" className="flex flex-col gap-2.5">
          {PLATFORM_ROWS.map((row) => (
            <div
              key={row.id}
              data-testid={`book-direct-save-row-${row.id}`}
              className="flex items-center justify-between gap-4 rounded-xl border border-emerald/10 bg-sand px-5 py-4"
            >
              <div className="min-w-0">
                <p className="font-semibold text-emerald">{row.name}</p>
                <p className="text-xs text-emerald-soft mt-0.5">{row.note}</p>
              </div>
              <p className="font-display text-2xl text-emerald-soft/70 shrink-0">{row.price}</p>
            </div>
          ))}
          <div
            data-testid="book-direct-save-row-direct"
            className="flex items-center justify-between gap-4 rounded-xl bg-emerald px-5 py-4 text-sand shadow-[0_16px_40px_-20px_rgba(15,26,21,0.5)]"
          >
            <div className="min-w-0">
              <p className="font-semibold flex flex-wrap items-center gap-2">
                Book direct
                <span data-testid="book-direct-save-badge" className="rounded-full bg-gold px-2.5 py-0.5 text-[11px] font-semibold text-emerald">
                  Save up to $225
                </span>
              </p>
              <p className="text-xs text-sand/70 mt-0.5">No platform fees — ever</p>
            </div>
            <p className="font-display text-2xl text-gold shrink-0">$1,500</p>
          </div>
        </div>
        <p className="text-xs text-emerald-soft/80 leading-relaxed mt-6 max-w-md">
          Illustrative example based on a $1,500 stay before taxes. Platform fees vary by dates, property and stay length — but when you book direct, the rate you see is the rate you pay.
        </p>
      </div>
    </motion.div>
  );
};

export default BookDirectSave;
